import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';

import { DeleteUsersRequest, UpdateBulkUsersResponse } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { UserWithRolesResponse } from 'projects/shared/services/inspection-companies/models/users/user-with-roles.response';
import { UsersDeletedResponse } from 'projects/shared/services/inspection-companies/models/users/users-deleted-response';
import { ReinvitePendingUsersModel } from 'projects/shared/services/invitations/models/reinvite-pending-users.model';

import { EditUsersBulkModalComponent } from '../edit/edit-users-bulk-modal.component';
import { DeleteUsersBulkModalComponent } from '../delete/delete-users-bulk-modal.component';
import { SecurityService } from 'projects/shared/services/security.service';
import { Permission } from 'projects/shared/directives/if-permission.directive';

@Component({
  selector: 'shared-bulk-user-actions-button',
  standalone: false,
  // Since we're only responding to input/output properties, OnPush has a better performance.
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<mac-button
      icon="menu"
      variant="secondary"
      class="ml-1"
      *ngIf="showMoreActions"
      nz-dropdown
      [nzDropdownMenu]="bulkActionsDropDown"
      nzTrigger="click">
    </mac-button>
    <nz-dropdown-menu #bulkActionsDropDown="nzDropdownMenu">
      <ul nz-menu>
        <li nz-menu-item *ngIf="showResendInvitations" (click)="resendInvitations()">
          <mac-icon icon="mail" style="margin-right: 0.25rem;"></mac-icon>
          {{ 'Portal.InspectionCompanies.Users.All.BulkActions.ResendInvitations.Label' | translate }}
        </li>
        <li nz-menu-item *ngIf="showBulkDelete" (click)="openDeleteModal()">
          <mac-icon icon="delete" style="margin-right: 0.25rem;"></mac-icon>
          {{ 'Portal.InspectionCompanies.Users.All.BulkActions.Delete.Label' | translate }}
        </li>
        <li nz-menu-item *ngIf="bulkUpdateUsersAction" (click)="openBulkEditUsersModal()">
          <mac-icon icon="edit" style="margin-right: 0.25rem;"></mac-icon>
          {{ 'Portal.InspectionCompanies.Users.All.BulkActions.BulkUpdate.Label' | translate }}
        </li>
      </ul>
    </nz-dropdown-menu>
    <shared-delete-users-bulk-modal
      #deleteBulkUsersModal
      [deleteUsersAction]="deleteUsersAction"
      [(isVisible)]="deleteModalVisible"
      [usersToDelete]="Array.from(selectedUsers)"
      (usersDeleted)="onUsersDeleted($event)" />
    <shared-edit-users-bulk-user-modal
      #editBulkUsersModal
      [bulkUpdateUsersAction]="bulkUpdateUsersAction"
      [isVisible]="editBulkUsersModalVisible"
      (usersUpdated)="onBulkUsersUpdated()" />`,
})
export class BulkUserActionsButtonComponent {
  protected readonly Array = Array;

  deleteModalVisible = false;
  editBulkUsersModalVisible = false;

  constructor(private readonly securityService: SecurityService) {}

  /**
   * The set of users selected for bulk actions.
   */
  @Input() selectedUsers: Set<UserWithRolesResponse> = new Set<UserWithRolesResponse>();

  /**
   * The action to reinvite pending users.
   */
  @Input() reinvitePendingUsersAction: (model: ReinvitePendingUsersModel) => Observable<void>;

  /**
   * The action to delete users.
   */
  @Input() deleteUsersAction: (model: DeleteUsersRequest) => Observable<UsersDeletedResponse>;

  /**
   * The action to bulk update users.
   */
  @Input() bulkUpdateUsersAction: (file: File) => Observable<UpdateBulkUsersResponse>;

  /**
   * Event emitted when users are reinvited.
   * Returns undefined when the reinvitation is successful, the error message otherwise.
   */
  @Output() usersReinvited = new EventEmitter<string | undefined>();

  @Output() usersDeleted = new EventEmitter<boolean>();

  @Output() bulkUsersUpdated = new EventEmitter<void>();

  @ViewChild(EditUsersBulkModalComponent) editBulkUsersModal?: EditUsersBulkModalComponent;

  @ViewChild(DeleteUsersBulkModalComponent) deleteBulkUsersModal?: DeleteUsersBulkModalComponent;

  get showMoreActions(): boolean {
    return this.showResendInvitations || this.showBulkDelete || this.showBulkUpdate;
  }

  get showResendInvitations(): boolean {
    return (
      this.hasPermission(Permission.InviteUsersToInspectionCompany) &&
      this.isAnyUserSelected &&
      this.hasOnlyPendingUsersSelected &&
      !!this.reinvitePendingUsersAction
    );
  }

  get showBulkDelete(): boolean {
    return (
      this.hasPermission(Permission.DeleteInspectionCompanyUser) &&
      this.isAnyUserSelected &&
      !!this.deleteUsersAction
    );
  }

  get showBulkUpdate(): boolean {
    return (
      this.hasPermission(Permission.UpdateInspectionCompanyUsers) &&
      this.bulkUpdateUsersAction !== undefined
    );
  }

  get isAnyUserSelected(): boolean {
    return this.selectedUsers.size > 0;
  }

  get hasOnlyPendingUsersSelected() {
    return Array.from(this.selectedUsers).every((user) => user.status === 'Pending');
  }

  resendInvitations() {
    const emails = Array.from(this.selectedUsers).map((user) => user.email);
    const request: ReinvitePendingUsersModel = {
      userEmails: emails,
    };
    
    this.reinvitePendingUsersAction(request).subscribe({
      next: () => {
        this.usersReinvited.emit();
      },
      error: (err) => {
        this.usersReinvited.emit(err?.error?.message);
      },
    });
  }

  openDeleteModal(): void {
    this.deleteBulkUsersModal?.openModal();
  }

  openBulkEditUsersModal(): void {
    this.editBulkUsersModal?.openModal();
  }

  onUsersDeleted(reloadNeeded: boolean): void {
    this.deleteBulkUsersModal?.closeModal();
    this.usersDeleted.emit(reloadNeeded);
  }

  onBulkUsersUpdated(): void {
    this.editBulkUsersModal?.closeModal();
    this.bulkUsersUpdated.emit();
  }

  private hasPermission(key: string): boolean {
    return this.securityService.hasPermission(key);
  }
}
